import React, { useEffect, useMemo } from 'react';
import './TransactionsTable.scss';
import TransactionsTableTableSearch from './TransactionsTableTableSearch';
import TableHeader from './TransactionsTableTableHeader';
import TransactionsTableTableRow from './TransactionsTableTableRow';
import TableFooter from './TransactionsTableFooter';
import { useTransactionsTableContext } from '../Chart/context/TransactionsTableContext';
import { AskRequest, ReportTransactionDataRow } from '@hone-automation/common';
import { useGetReportAttachments } from 'hooks';
import { UIReportTransaction } from 'types';
import { prefixClass } from 'lib/utils';

import TransactionTableEmpty from './TransactionTableEmpty';
import { useReportsStore } from 'hooks/useReportsStore';
import { filterTransactions, sortTransactions, findFlaggedCell } from './utils';
import { useOpenFlagInPanel } from 'hooks/useOpenFlagInPanel';

export type Transaction = {
  period?: string;
  date: string;
  type: string;
  reference?: string;
  name: string;
  memo: string;
  dueDays: number | 'paid';
  dueDate?: string;
  amount: number;
  links?: number;
  balance: number;
  highlight?: boolean;
  eventId?: string;
};

const TransactionsTable: React.FC = () => {
  const {
    transactions,
    reportRows,
    total,
    flaggedTransactions,
    flaggedCells,
    sortKey,
    sortDir,
    search,
    selectedTransaction,
    cellWithOutTransactions,
    resolvedLocationId,
  } = useTransactionsTableContext();
  const selectedReport = useReportsStore(state => state.selectedReport);
  const { openFlagPanel } = useOpenFlagInPanel();

  const {
    mutate: getAttachments,
    isPending: attachmentsLoading,
    data: attachmentsData,
  } = useGetReportAttachments(resolvedLocationId);
  // Filter transactions based on search
  const filteredTransactions = useMemo(() => {
    return filterTransactions(transactions, search);
  }, [transactions, search]);

  // Sort transactions based on sortKey and sortDir
  const sortedTransactions = useMemo(() => {
    return sortTransactions(filteredTransactions, sortKey, sortDir);
  }, [filteredTransactions, sortKey, sortDir]);

  useEffect(() => {
    const handler = setTimeout(() => {
      getAttachments(reportRows as ReportTransactionDataRow[]);
    }, 3000); // Debounce delay

    return () => {
      clearTimeout(handler); // Cleanup timeout on unmount
    };
  }, [getAttachments, reportRows]);

  const getCellTitle = () => {
    if (!selectedTransaction) return 'Transaction Details';
    const lastPath =
      selectedTransaction.path && selectedTransaction.path.length
        ? selectedTransaction.path[selectedTransaction.path.length - 1]
        : undefined;
    return lastPath || selectedTransaction.baseRef || 'Transaction Details';
  };

  const onRowClick = (transaction: Transaction) => {
    if (!selectedTransaction || !resolvedLocationId) return;
    const flagged = flaggedTransactions?.find(flagged => {
      return Number(flagged.flaggedAmount) === Number(transaction.amount) && flagged.period === transaction.period;
    });
    const transformedTransaction = reportRows.find(row => row.eventId === transaction.eventId);

    const flaggedTransaction: UIReportTransaction = {
      ...transformedTransaction,
      flagged: flagged as AskRequest,
    } as any;

    openFlagPanel({
      cellTitle: getCellTitle(),
      cellDate: { start: transaction.period!, end: '' },
      cellAmount: transaction.amount || 0,
      parentTitle: getCellTitle(),
      resolvedLocationId,
      reportId: selectedTransaction.reportId || null,
      dataRows: reportRows as any[],
      audit: null,
      trxFlagged: (selectedTransaction as any).trxFlagged,
      transaction: flaggedTransaction,
    });
  };

  const currentCellIsFlagged = useMemo(() => {
    return findFlaggedCell(selectedTransaction, flaggedCells, selectedReport?.type);
  }, [flaggedCells, selectedTransaction, selectedReport?.type]);

  // Get the cell's flagged transactions array for row-level matching
  const cellTrxFlagged = useMemo(() => {
    if (!selectedTransaction) return [];
    return ((selectedTransaction as any).trxFlagged as AskRequest[]) || [];
  }, [selectedTransaction]);

  const handleFlagTransaction = () => {
    if (!selectedTransaction || !resolvedLocationId) return;

    const flaggedTransaction: UIReportTransaction = {
      amount: selectedTransaction.amount,
      txnDate: selectedTransaction.date,
      flagged: currentCellIsFlagged as AskRequest,
    } as any;

    openFlagPanel({
      cellTitle: getCellTitle(),
      cellDate: { start: selectedTransaction.date!, end: '' },
      cellAmount: selectedTransaction.amount || 0,
      parentTitle: getCellTitle(),
      resolvedLocationId,
      reportId: selectedTransaction.reportId || null,
      dataRows: reportRows as any[],
      audit: null,
      trxFlagged: (selectedTransaction as any).trxFlagged,
      transaction: flaggedTransaction,
    });
  };

  const prefix = prefixClass('transactions-table');

  const cellLabel = useMemo(() => {
    if (!selectedTransaction) return undefined;
    const path = selectedTransaction.path;
    const last = path && path.length ? path[path.length - 1] : undefined;
    return last || selectedTransaction.amountTitle || selectedTransaction.refTitle || selectedTransaction.baseRef;
  }, [selectedTransaction]);

  return (
    <>
      {cellWithOutTransactions && selectedTransaction ? (
        <TransactionTableEmpty
          cellLabel={cellLabel}
          selectedTransaction={selectedTransaction}
          currentCellIsFlagged={currentCellIsFlagged}
          onFlag={handleFlagTransaction}
        />
      ) : (
        <div className={prefix()}>
          <TransactionsTableTableSearch onFlagTransaction={handleFlagTransaction} flaggedCell={currentCellIsFlagged} />
          <div className={prefix('wrapper')}>
            {sortedTransactions.length > 0 ? (
              <table className={prefix('content')}>
                <TableHeader />
                <tbody className={prefix('body')}>
                  {sortedTransactions.map((t, i) => (
                    <TransactionsTableTableRow
                      key={t.eventId || `${t.date}-${t.amount}-${t.name}-${i}`}
                      transaction={t}
                      attachmentsLoading={attachmentsLoading}
                      attachmentsData={attachmentsData}
                      onRowClick={onRowClick}
                      cellFlaggedStatus={currentCellIsFlagged}
                      cellTrxFlagged={cellTrxFlagged}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={prefix('no-transactions')}>No transactions found for search "{search}"</div>
            )}
          </div>
          <div className={prefix('footer-container')}>
            <div className={prefix('footer-gradient')}>
              <TableFooter />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionsTable;
